import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, Send, ArrowLeft, History, Paperclip, X, FileText } from "lucide-react";
import { uploadAsset } from "../components/Assets";
import { useBrands } from "../context/BrandContext";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { AiHtml, Spinner } from "../components/AiHtml";
import { useAuth } from "../context/AuthContext";
import { api, fileUrl, handleErr, streamChat } from "../lib/api";

const groupOf = (d) => {
  const days = Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 864e5);
  return days <= 0 ? "Today" : days === 1 ? "Yesterday" : days < 7 ? "Previous 7 days" : days < 30 ? "Previous 30 days" : "Older";
};

const HistoryPanel = ({ chats, activeId, onSelect, onNew, onRename, onDelete, q, setQ }) => {
  const groups = chats.reduce((g, c) => ((g[groupOf(c.date)] ||= []).push(c), g), {});
  return (
    <div className="flex flex-col h-full">
      <button data-testid="new-chat-btn" onClick={onNew} className="m-3 flex items-center justify-center gap-2 py-2.5 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium transition-colors"><Plus size={16} /> New Chat</button>
      <div className="px-3 relative">
        <Search size={14} className="absolute left-6 top-3 text-slate-500" />
        <Input data-testid="chat-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search history" className="pl-8 h-9 text-sm" />
      </div>
      <div className="flex-1 overflow-y-auto scroll-thin px-2 py-3 space-y-4" data-testid="chat-history-list">
        {chats.length === 0 && <p className="text-xs text-slate-500 px-3">No chats yet.</p>}
        {Object.entries(groups).map(([g, list]) => (
          <div key={g}>
            <p className="label-mono px-3 mb-1 !text-slate-500">{g}</p>
            {list.map((c) => (
              <div key={c.chatId} data-testid={`chat-item-${c.chatId}`} className={`group flex items-center gap-1 rounded-lg px-3 py-2 text-sm cursor-pointer ${activeId === c.chatId ? "bg-orange-500/10 text-orange-300" : "text-slate-400 hover:bg-slate-800/60"}`} onClick={() => onSelect(c.chatId)}>
                <span className="flex-1 truncate">{c.title}</span>
                <button data-testid={`chat-rename-${c.chatId}`} onClick={(e) => { e.stopPropagation(); onRename(c); }} className="opacity-60 lg:opacity-0 group-hover:opacity-100 hover:text-orange-400"><Pencil size={13} /></button>
                <button data-testid={`chat-delete-${c.chatId}`} onClick={(e) => { e.stopPropagation(); onDelete(c); }} className="opacity-60 lg:opacity-0 group-hover:opacity-100 hover:text-red-400"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const Attachment = ({ a, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 max-w-[200px] text-xs px-2 py-1 rounded-lg bg-slate-900 border border-slate-700">
    {a.contentType?.startsWith("image/") ? <img src={fileUrl("asset", a.assetId)} alt="" className="w-6 h-6 rounded object-cover" /> : <FileText size={13} className="text-orange-400" />}
    <span className="truncate">{a.name}</span>
    {onRemove && <button onClick={onRemove} className="text-slate-500 hover:text-red-400"><X size={12} /></button>}
  </span>
);

const Bubble = ({ m, i }) =>
  m.role === "user" ? (
    <div className="flex justify-end"><div data-testid={`chat-msg-user-${i}`} className="max-w-[85%] bg-orange-500/15 border border-orange-500/25 rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm text-slate-100 whitespace-pre-wrap">
      {m.attachments?.length > 0 && <div className="flex flex-wrap gap-1.5 mb-2">{m.attachments.map((a) => <Attachment key={a.assetId} a={a} />)}</div>}
      {m.content}</div></div>
  ) : (
    <div data-testid={`chat-msg-ai-${i}`} className="card-n px-5 py-4 max-w-[95%]"><AiHtml text={m.content || "..."} /></div>
  );

export default function EmployeeChat() {
  const { id } = useParams();
  const { config, refresh, user } = useAuth();
  const emp = config?.employees.find((e) => e.id === id);
  const [chats, setChats] = useState([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const { active: brand } = useBrands();
  const attach = async (list) => {
    setUploading(true);
    try {
      for (const f of Array.from(list).slice(0, 5 - files.length)) {
        const a = await uploadAsset(f, { brandId: brand?.brandId || "" });
        setFiles((x) => [...x, a]);
      }
    } catch (e) { handleErr(e); } finally { setUploading(false); }
  };
  const endRef = useRef(null);

  const load = useCallback(() => api.get("/chats", { params: { employee: id, q } }).then((r) => setChats(r.data)), [id, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setActive(null); setMsgs([]); }, [id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const select = async (cid) => {
    setActive(cid);
    setShowHist(false);
    const { data } = await api.get(`/chats/${cid}`);
    setMsgs(data.messages);
  };
  const newChat = () => { setActive(null); setMsgs([]); setShowHist(false); };
  const rename = async (c) => {
    const t = window.prompt("Rename chat", c.title);
    if (t) { await api.patch(`/chats/${c.chatId}`, { title: t }); load(); }
  };
  const del = async (c) => {
    if (!window.confirm("Delete this chat?")) return;
    await api.delete(`/chats/${c.chatId}`);
    if (active === c.chatId) newChat();
    load();
    toast.success("Chat deleted");
  };

  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    if (!user.isAdmin && user.credits < 1) return window.dispatchEvent(new CustomEvent("namo:paywall", { detail: "chat" }));
    setBusy(true);
    setText("");
    const sent = files;
    setFiles([]);
    let cid = active;
    try {
      if (!cid) { cid = (await api.post("/chats", { employee: id })).data.chatId; setActive(cid); }
      setMsgs((m) => [...m, { role: "user", content: t, attachments: sent }, { role: "assistant", content: "" }]);
      await streamChat(cid, t, sent.map((a) => a.assetId), (chunk) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + chunk }; return c; }));
    } catch (e) {
      handleErr(e);
      if (!active && cid) { api.delete(`/chats/${cid}`).catch(() => {}); setActive(null); }
      setMsgs((m) => (m.length && m[m.length - 1].role === "assistant" && !m[m.length - 1].content ? m.slice(0, -2) : m));
    } finally {
      setBusy(false);
      load();
      refresh();
    }
  };

  if (!config) return <Spinner label="Loading your AI team..." />;
  if (!emp) return <div className="card-n p-10 text-center text-slate-400" data-testid="employee-not-found">Employee not found. <Link to="/employees" className="text-orange-400">Back to team</Link></div>;
  const panel = <HistoryPanel chats={chats} activeId={active} onSelect={select} onNew={newChat} onRename={rename} onDelete={del} q={q} setQ={setQ} />;
  return (
    <div className="flex h-[calc(100vh-8rem)] lg:h-[calc(100vh-9rem)] card-n overflow-hidden">
      <aside className="hidden md:flex w-72 border-r border-slate-800 flex-col bg-[#0B1120]">{panel}</aside>
      {showHist && <div className="md:hidden fixed inset-0 z-50 bg-[#0B1120] pt-4" data-testid="mobile-history">{panel}<button className="absolute top-4 right-4 text-slate-400 text-sm" onClick={() => setShowHist(false)}>Close</button></div>}
      <section className="flex-1 flex flex-col min-w-0">
        <div className="h-16 border-b border-slate-800 flex items-center gap-3 px-4">
          <Link to="/employees" className="text-slate-500 hover:text-slate-200"><ArrowLeft size={18} /></Link>
          <div className="relative w-9 h-9 rounded-full bg-orange-500 grid place-items-center font-display font-bold">{emp.name[0]}<span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-[#0B1120]" /></div>
          <div className="min-w-0 flex-1"><p className="font-semibold text-sm flex items-center gap-2" data-testid="chat-employee-name">{emp.name}<span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 font-normal"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />Active</span></p><p className="text-xs text-slate-500 truncate">{emp.role} · 1 credit / message</p></div>
          <button className="md:hidden text-slate-400" onClick={() => setShowHist(true)} data-testid="mobile-history-btn"><History size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto scroll-thin p-4 sm:p-6 space-y-4" data-testid="chat-messages">
          {msgs.length === 0 && (
            <div className="h-full grid place-items-center text-center text-slate-500"><div><p className="font-display text-2xl text-slate-200">Ask {emp.name} anything</p><p className="text-sm mt-2 max-w-md">{emp.desc}</p></div></div>
          )}
          {msgs.map((m, i) => <Bubble key={i} m={m} i={i} />)}
          <div ref={endRef} />
        </div>
        {files.length > 0 && <div className="px-4 pt-3 flex flex-wrap gap-2" data-testid="chat-attachments">{files.map((a) => <Attachment key={a.assetId} a={a} onRemove={() => setFiles((x) => x.filter((y) => y.assetId !== a.assetId))} />)}</div>}
        <div className="p-3 sm:p-4 border-t border-slate-800 flex gap-2 items-end">
          <button data-testid="chat-attach-btn" title="Attach logo, image, PDF, DOCX, TXT" disabled={uploading || files.length >= 5} onClick={() => fileRef.current?.click()} className="h-11 w-11 shrink-0 rounded-full border border-slate-700 hover:border-orange-500 hover:text-orange-400 grid place-items-center disabled:opacity-40">
            {uploading ? <span className="w-4 h-4 border-2 border-slate-600 border-t-orange-500 rounded-full animate-spin" /> : <Paperclip size={17} />}
          </button>
          <input ref={fileRef} data-testid="chat-file-input" type="file" multiple accept=".png,.jpg,.jpeg,.webp,.pdf,.docx,.txt,.md,.csv" className="hidden" onChange={(e) => { attach(e.target.files); e.target.value = ""; }} />
          <Textarea data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={`Message ${emp.name}...`} rows={2} className="resize-none" />
          <button data-testid="chat-send-btn" disabled={busy || !text.trim()} onClick={send} className="h-11 w-11 shrink-0 rounded-full bg-orange-500 hover:bg-orange-600 disabled:opacity-40 grid place-items-center"><Send size={17} /></button>
        </div>
      </section>
    </div>
  );
}
